'use client';

import React from 'react';
import { Archive, FileStack, ShieldCheck, Users, ScanLine, Award, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

const compactRainbowGradient =
  'linear-gradient(90deg, #06B6D4 0%, #2563EB 14%, #7C3AED 28%, #EC4899 42%, #F97316 56%, #FACC15 68%, #22C55E 82%, #14B8A6 100%)';

const rainbowText = {
  background: compactRainbowGradient,
  WebkitBackgroundClip: 'text',
  color: 'transparent',
};

const HIERARCHY_STYLES = [
  { color: '#64748b', labelKey: 'landing_record_proof.level1_title', descKey: 'landing_record_proof.level1_desc' },
  { color: '#0891b2', labelKey: 'landing_record_proof.level2_title', descKey: 'landing_record_proof.level2_desc' },
  { color: '#059669', labelKey: 'landing_record_proof.level3_title', descKey: 'landing_record_proof.level3_desc' },
  { color: '#d97706', labelKey: 'landing_record_proof.level4_title', descKey: 'landing_record_proof.level4_desc' },
];

const STEP_ICONS = [
  <Archive size={22} key="archive" />,
  <FileStack size={22} key="filestack" />,
  <Users size={22} key="users" />,
  <ScanLine size={22} key="scanline" />,
  <Award size={22} key="award" />,
];

const STEP_KEYS = [
  { n: '1', titleKey: 'landing_record_proof.step1_title', bodyKey: 'landing_record_proof.step1_desc', highlight: false },
  { n: '2', titleKey: 'landing_record_proof.step2_title', bodyKey: 'landing_record_proof.step2_desc', highlight: false },
  { n: '3', titleKey: 'landing_record_proof.step3_title', bodyKey: 'landing_record_proof.step3_desc', highlight: false },
  { n: '4', titleKey: 'landing_record_proof.step4_title', bodyKey: 'landing_record_proof.step4_desc', highlight: false },
  { n: '5', titleKey: 'landing_record_proof.step5_title', bodyKey: 'landing_record_proof.step5_desc', highlight: true },
];

export default function RecordToProofSection() {
  const { t } = useTranslation();
  return (
    <section id="from-record-to-proof" className="scroll-mt-24 bg-white py-20 sm:py-24">
      <div className="mx-auto max-w-screen-2xl px-6 lg:px-10 2xl:px-16">
        <div className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-50 px-4 py-2">
            <ShieldCheck size={14} className="text-cyan-700" />
            <span className="text-sm font-semibold text-cyan-800">{t('landing_record_proof.eyebrow' as any)}</span>
          </div>
          <h2
            className="mb-4 inline-block text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-5xl"
            style={rainbowText}
          >
            {t('landing_record_proof.title' as any)}
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            {t('landing_record_proof.subtitle' as any)}
          </p>
        </div>

        <div className="mb-14 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {HIERARCHY_STYLES.map((h, i) => (
            <div key={h.labelKey} className="relative">
              {i < HIERARCHY_STYLES.length - 1 && (
                <div
                  className="absolute right-0 top-1/2 z-0 hidden w-4 translate-x-full -translate-y-1/2 sm:block"
                  aria-hidden
                >
                  <ArrowRight size={16} className="text-slate-300" />
                </div>
              )}
              <div
                className="relative z-10 rounded-xl border p-4 text-center"
                style={{
                  borderColor: `${h.color}40`,
                  background: `${h.color}08`,
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: h.color }}
                >
                  {t(h.labelKey as any)}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-slate-600">{t(h.descKey as any)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {STEP_KEYS.map((s, i) => (
            <div
              key={s.n}
              className={`rounded-2xl border p-5 transition-shadow hover:shadow-md ${
                s.highlight
                  ? 'border-amber-200/80 bg-gradient-to-br from-amber-50/90 to-white'
                  : 'border-slate-100 bg-slate-50/80'
              }`}
            >
              <div className="mb-3 flex items-center justify-between">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
                  style={{
                    background: s.highlight
                      ? 'linear-gradient(135deg, #d97706, #c9a84c)'
                      : 'linear-gradient(135deg, #0891b2, #2563eb)',
                  }}
                >
                  {STEP_ICONS[i]}
                </span>
                <span className="font-mono text-xs font-bold text-slate-400">{s.n}</span>
              </div>
              <h3 className="mb-2 text-sm font-bold text-[#002B7F]">{t(s.titleKey as any)}</h3>
              <p className="text-xs leading-relaxed text-slate-600">{t(s.bodyKey as any)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
